/*
    Which way a drill's shots are angled, and therefore whether mirroring it
    means anything.

    A lacrosse drill aims at a cage, not at a service court, so the deuce/ad
    side picker every other sport shows has nothing to name. What a coach
    actually wants to know is whether the drill attacks one side of the cage -
    because only then is there another side to feed.

    Pure, and takes `unknown`: a workout config is jsonb, every app declares its
    own shape, and the four shapes below are all in production at once. Reading
    defensively here is the same bargain workoutSetup.ts makes.
*/
import type {
    CuratedWorkoutConfig,
    LeveledWorkoutConfig,
    LevelNumber,
} from "@volley/shared/apps/curated-workout-models";

/**
 * How a drill's shots sit relative to the cage's centreline.
 *
 * - `one-sided` - every angled shot leans the same way, so the mirrored drill
 *   is a real second drill and `dual` alternates between them.
 * - `mixed` - the drill already works both sides. Mirroring would swap left for
 *   right and change nothing worth offering.
 * - `none` - nothing is angled at all (or there are no shots). There is no
 *   second side to feed, so no choice is offered.
 */
export type ShotSpread = "one-sided" | "mixed" | "none";

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The `pan` of anything in `shots` that carries a numeric one.
 *
 * Shots are read one field deep and nothing else about them is trusted, so a
 * config holding a half-built shot contributes what it has rather than throwing.
 */
function pansFromShots(value: unknown): number[] {
    if (!Array.isArray(value)) return [];

    return value.flatMap((shot) =>
        isRecord(shot) && typeof shot.pan === "number" ? [shot.pan] : [],
    );
}

/**
 * Every shot pan in a stored workout config, whatever shape it is in.
 *
 * The four shapes below are the ones reachable from the play screens that show
 * the side control, verified against the `*Config` types in @volley/shared:
 *
 * | `config.shots[]`           | 4-multi-shot, 11-module, 17-padel |
 * | `config.levels[n].shots[]` | 9-multi-level                     |
 * | `config.shot`              | 5-single-shot                     |
 * | `config.sequence[].shots[]`| 15-aoi-triggered                  |
 *
 * `level` narrows the multi-level case to the level that will actually be
 * played. Without it every level counts, which is the right answer for a screen
 * that has not chosen one yet.
 */
export function collectPans(config: unknown, level?: LevelNumber): number[] {
    if (!isRecord(config)) return [];

    // 9-multi-level. First because a leveled config has no top-level `shots`,
    // and reading the wrong level is worse than reading none.
    if (isRecord(config.levels)) {
        const { levels } = config as unknown as LeveledWorkoutConfig;
        const wanted =
            level !== undefined ? [levels[level]] : Object.values(levels);

        return wanted.flatMap((entry) =>
            isRecord(entry) ? pansFromShots(entry.shots) : [],
        );
    }

    // 15-aoi-triggered: shots hang off each triggered sequence.
    if (Array.isArray(config.sequence)) {
        return config.sequence.flatMap((entry) =>
            isRecord(entry) ? pansFromShots(entry.shots) : [],
        );
    }

    // 4-multi-shot, 11-module, 17-padel.
    if (Array.isArray(config.shots)) {
        return pansFromShots((config as CuratedWorkoutConfig).shots);
    }

    // 5-single-shot: one shot, not a list.
    if (isRecord(config.shot) && typeof config.shot.pan === "number") {
        return [config.shot.pan];
    }

    return [];
}

/**
 * Whether these pans lean one way, both ways, or neither.
 *
 * A pan of exactly 0 aims straight down the machine's centreline, at the cage
 * itself. It belongs to no side, so it neither makes a drill one-sided nor
 * spoils one that is - a drill of left-side shots with a few straight feeds
 * still has a mirror image worth offering.
 */
export function shotSpread(pans: number[]): ShotSpread {
    const angled = pans.filter((pan) => Number.isFinite(pan) && pan !== 0);
    if (angled.length === 0) return "none";

    const leansRight = angled[0] > 0;
    return angled.every((pan) => pan > 0 === leansRight)
        ? "one-sided"
        : "mixed";
}

/** `collectPans` and `shotSpread` in one call, for the usual case. */
export function workoutShotSpread(
    config: unknown,
    level?: LevelNumber,
): ShotSpread {
    return shotSpread(collectPans(config, level));
}
